// Constrained inline-markdown subset for task titles: **bold**, *italic*,
// ~~strikethrough~~, `code`, [link](https://url), and @[Name](user:ID) /
// @[Name](contact:ID) / @[Name](company:ID) mentions. Mirrors app/services/tasks/inline_markdown.rb
// — keep both renderers byte-for-byte in sync.
//
// mdToHtml renders markdown source to the sanitized HTML subset used by the
// outline (strong/em/del/code/a only, text HTML-escaped). domToMarkdown
// serializes a WYSIWYG editor's DOM back to markdown source, backslash-
// escaping typed specials so literal *, ~, `, [ and \ never corrupt.
//
// Colour spans (feedback #209): {color:red}text{/color},
// {highlight:yellow}text{/highlight}, {badge:green}text{/badge} render to
// <span class="tj-color tj-color--red"> etc. A "{" is escaped only where it
// would start or end such a token.

const SPECIALS = ["\\", "*", "~", "`", "[", "]", "{"]
export const STYLE_KINDS = ["color", "highlight", "badge"]
export const STYLE_COLORS = ["red", "orange", "yellow", "green", "blue", "purple", "pink", "gray"]
const STYLE_OPEN_PATTERN = /^\{(color|highlight|badge):(red|orange|yellow|green|blue|purple|pink|gray)\}/
const STYLE_BRACE = /\{(?=\/?(?:color|highlight|badge)[:}])/g
const SAFE_HREF = /^(https?:\/\/|mailto:)/i
// A bare http(s) URL is a link too; same rules as InlineMarkdown::BARE_URL.
const BARE_URL = /^https?:\/\/[^\s<>"]+/
const BARE_URL_BOUNDARY = /[ \t\n(]/
const URL_TRAILING = /[.,;:!?'"]+$/
const LINK_PATTERN = /^\[((?:\\.|[^\]\\])*)\]\(([^)\s]+)\)/
// @[Display Name](user:ID) / (contact:ID) / (company:ID) — a mention, mirrors
// the Ruby side. Group 1 is the label, group 2 the kind, group 3 the id.
const MENTION_PATTERN = /^@\[((?:\\.|[^\]\\])*)\]\((user|contact|company):(\d+)\)/

export function escapeMd(text) {
    return String(text ?? "").replace(/([\\*~`[\]])/g, "\\$1").replace(STYLE_BRACE, "\\{")
}

export function mdToHtml(source) {
    return renderHtml(parse(String(source ?? ""), true))
}

// Task notes: the same inline subset, line by line, with "- " / "* " lines
// drawn as bullets. Lines stay "\n"-joined (the note is white-space: pre-wrap).
// Mirrors InlineMarkdown.notes_html.
const NOTE_BULLET = /^([ \t]*)[-*•][ \t]+/

export function notesToHtml(source) {
    return String(source ?? "").split("\n").map((line) => {
        const bullet = line.match(NOTE_BULLET)
        if (!bullet) return mdToHtml(line)
        return `${bullet[1]}<span class="tj-note-bullet">•</span> ${mdToHtml(line.slice(bullet[0].length))}`
    }).join("\n")
}

export function mdToPlain(source) {
    return renderPlain(parse(String(source ?? ""), true))
}

function parse(md, allowLinks) {
    const nodes = []
    let buffer = ""
    const flush = () => {
        if (buffer !== "") nodes.push(buffer)
        buffer = ""
    }
    let i = 0
    while (i < md.length) {
        const ch = md[i]
        let close
        let styleMatch
        if (ch === "\\" && SPECIALS.includes(md[i + 1])) {
            buffer += md[i + 1]
            i += 2
        } else if (md.startsWith("``", i) && (close = md.indexOf("``", i + 2)) > i + 2) {
            flush()
            nodes.push({ type: "code", value: md.slice(i + 2, close) })
            i = close + 2
        } else if (ch === "`" && (close = md.indexOf("`", i + 1)) > i + 1) {
            flush()
            nodes.push({ type: "code", value: md.slice(i + 1, close) })
            i = close + 1
        } else if (md.startsWith("**", i) && (close = closingDelim(md, "**", i + 2)) !== null) {
            flush()
            nodes.push({ type: "strong", children: parse(md.slice(i + 2, close), allowLinks) })
            i = close + 2
        } else if (md.startsWith("~~", i) && (close = closingDelim(md, "~~", i + 2)) !== null) {
            flush()
            nodes.push({ type: "del", children: parse(md.slice(i + 2, close), allowLinks) })
            i = close + 2
        } else if (ch === "*" && (close = closingDelim(md, "*", i + 1)) !== null) {
            flush()
            nodes.push({ type: "em", children: parse(md.slice(i + 1, close), allowLinks) })
            i = close + 1
        } else if (ch === "{" && (styleMatch = STYLE_OPEN_PATTERN.exec(md.slice(i))) && (close = styleClose(md, styleMatch[1], i + styleMatch[0].length)) !== null) {
            flush()
            nodes.push({ type: "style", kind: styleMatch[1], color: styleMatch[2], children: parse(md.slice(i + styleMatch[0].length, close), allowLinks) })
            i = close + `{/${styleMatch[1]}}`.length
        } else if (ch === "@" && allowLinks && MENTION_PATTERN.test(md.slice(i))) {
            const match = MENTION_PATTERN.exec(md.slice(i))
            flush()
            nodes.push({ type: "mention", kind: match[2], targetId: match[3], children: parse(match[1], false) })
            i += match[0].length
        } else if (ch === "h" && allowLinks && (i === 0 || BARE_URL_BOUNDARY.test(md[i - 1])) && BARE_URL.test(md.slice(i))) {
            const url = trimBareUrl(BARE_URL.exec(md.slice(i))[0])
            flush()
            nodes.push({ type: "autolink", href: url.replace(/\\([\\*~`[\]{])/g, "$1") })
            i += url.length
        } else if (ch === "[" && allowLinks) {
            const match = LINK_PATTERN.exec(md.slice(i))
            if (match && SAFE_HREF.test(match[2])) {
                flush()
                nodes.push({ type: "link", href: match[2], children: parse(match[1], false) })
                i += match[0].length
            } else {
                buffer += ch
                i += 1
            }
        } else {
            buffer += ch
            i += 1
        }
    }
    flush()
    return nodes
}

// Same rules as the Ruby side: no space just inside either edge, skip
// backslash-escaped occurrences, never match an empty span.
function closingDelim(md, delim, from) {
    if (md[from] === undefined || md[from] === " ") return null
    let idx = from
    while ((idx = md.indexOf(delim, idx)) !== -1) {
        let backslashes = 0
        let j = idx - 1
        while (j >= 0 && md[j] === "\\") {
            backslashes += 1
            j -= 1
        }
        if (backslashes % 2 === 0) {
            if (idx === from) return null
            if (md[idx - 1] !== " ") return idx
        }
        idx += 1
    }
    return null
}

// Drops trailing punctuation, then a ")" with no "(" to close it.
function trimBareUrl(url) {
    for (;;) {
        let trimmed = url.replace(URL_TRAILING, "")
        if (trimmed.endsWith(")") && (trimmed.match(/\(/g) || []).length < (trimmed.match(/\)/g) || []).length) trimmed = trimmed.slice(0, -1)
        if (trimmed === url) return trimmed
        url = trimmed
    }
}

// Index of the "{/kind}" closing a span whose content starts at `from`,
// counting nested same-kind spans and skipping escapes; null if it never
// closes or would be empty. Same rules as InlineMarkdown.style_close.
function styleClose(md, kind, from) {
    const openToken = `{${kind}:`
    const closeToken = `{/${kind}}`
    let depth = 1
    let idx = from
    while (idx < md.length) {
        if (md[idx] === "\\") {
            idx += 2
            continue
        }
        if (md.startsWith(openToken, idx) && STYLE_OPEN_PATTERN.test(md.slice(idx))) {
            depth += 1
        } else if (md.startsWith(closeToken, idx)) {
            depth -= 1
            if (depth === 0) return idx === from ? null : idx
        }
        idx += 1
    }
    return null
}

function escapeHtml(text) {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
}

function renderHtml(nodes) {
    return nodes.map((node) => {
        if (typeof node === "string") return escapeHtml(node)
        switch (node.type) {
            case "code": return `<code>${escapeHtml(node.value)}</code>`
            case "strong": return `<strong>${renderHtml(node.children)}</strong>`
            case "em": return `<em>${renderHtml(node.children)}</em>`
            case "del": return `<del>${renderHtml(node.children)}</del>`
            case "style": return `<span class="tj-${node.kind} tj-${node.kind}--${node.color}">${renderHtml(node.children)}</span>`
            case "autolink": return `<a href="${escapeHtml(node.href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(node.href)}</a>`
            case "link": return `<a href="${escapeHtml(node.href)}" target="_blank" rel="noopener noreferrer">${renderHtml(node.children)}</a>`
            case "mention":
                // Byte-identical to Tasks::InlineMarkdown#node_html.
                if (node.kind === "contact") return `<span class="task-mention task-mention--contact" data-mention="contact:${node.targetId}" contenteditable="false">@${renderHtml(node.children)}</span>`
                if (node.kind === "company") return `<span class="task-mention task-mention--company" data-mention="company:${node.targetId}" contenteditable="false">${renderHtml(node.children)}</span>`
                return `<a href="/u/${node.targetId}" class="task-mention">@${renderHtml(node.children)}</a>`
            default: return ""
        }
    }).join("")
}

function renderPlain(nodes) {
    return nodes.map((node) => {
        if (typeof node === "string") return node
        if (node.type === "code") return node.value
        if (node.type === "autolink") return node.href
        if (node.type === "mention") return node.kind === "company" ? renderPlain(node.children || []) : `@${renderPlain(node.children || [])}`
        return renderPlain(node.children || [])
    }).join("")
}

// --- DOM -> markdown -------------------------------------------------------

export function domToMarkdown(root) {
    if (!root) return ""
    return serializeChildren(root, true)
}

function serializeChildren(node, allowLinks) {
    let out = ""
    node.childNodes.forEach((child) => {
        out += serializeNode(child, allowLinks)
    })
    return out
}

function serializeNode(node, allowLinks) {
    if (node.nodeType === Node.TEXT_NODE) return escapeMd(node.data)
    if (node.nodeType !== Node.ELEMENT_NODE) return ""

    const tag = node.tagName
    if (tag === "BR") return " "
    if (tag === "CODE") return serializeCode(node)

    // Boundary whitespace moves outside the delimiters ("**word **" would
    // not re-parse; "**word** " does).
    const wrap = (delim) => {
        const inner = serializeChildren(node, allowLinks)
        const m = inner.match(/^(\s*)([\s\S]*?)(\s*)$/)
        return m[2] === "" ? inner : `${m[1]}${delim}${m[2]}${delim}${m[3]}`
    }
    // Contact / company mention chip: a <span data-mention="contact:ID"> or
    // "company:ID" (non-link). Serialize back to the @[Name](kind:ID) token,
    // stripping a leading "@" (company chips draw their icon in CSS, so their
    // text is the bare name). Keyed on data-mention, checked before the
    // generic recursion.
    if (tag === "SPAN") {
        const m = (node.getAttribute("data-mention") || "").match(/^(contact|company):(\d+)$/)
        if (m) {
            const inner = serializeChildren(node, false).replace(/^@/, "")
            return inner.trim() === "" ? "" : `@[${inner}](${m[1]}:${m[2]})`
        }
        const style = styleOf(node)
        if (style) {
            const inner = serializeChildren(node, allowLinks)
            return inner === "" ? "" : `{${style.kind}:${style.color}}${inner}{/${style.kind}}`
        }
    }
    // User mention chip: an anchor to /u/ID (class task-mention). Serialize
    // back to the @[Name](user:ID) token, stripping the leading "@" from the
    // rendered label. Checked before the generic link branch.
    if (tag === "A") {
        const mentionId = mentionUserId(node)
        if (mentionId !== null) {
            const inner = serializeChildren(node, false).replace(/^@/, "")
            return inner.trim() === "" ? "" : `@[${inner}](user:${mentionId})`
        }
    }
    if (tag === "B" || tag === "STRONG") return wrap("**")
    if (tag === "I" || tag === "EM") return wrap("*")
    if (tag === "S" || tag === "STRIKE" || tag === "DEL") return wrap("~~")
    if (tag === "A" && allowLinks) {
        const href = node.getAttribute("href") || ""
        // An auto-linked bare URL goes back as the bare URL, not [url](url).
        if (/^https?:\/\//i.test(href) && node.textContent === href) return href
        const inner = serializeChildren(node, false)
        if (!SAFE_HREF.test(href) || inner.trim() === "") return inner
        const safe = href.replace(/\(/g, "%28").replace(/\)/g, "%29").replace(/ /g, "%20")
        return `[${inner}](${safe})`
    }
    return serializeChildren(node, allowLinks)
}

// { kind, color } for a colour/highlight/badge span (tj-<kind>--<color>),
// else null. Only palette names round-trip; anything else is dropped to text.
export function styleOf(node) {
    if (!node.classList) return null
    for (const cls of node.classList) {
        const m = cls.match(/^tj-(color|highlight|badge)--([a-z]+)$/)
        if (m && STYLE_COLORS.includes(m[2])) return { kind: m[1], color: m[2] }
    }
    return null
}

// The mentioned user id if this anchor is a mention chip, else null. Accepts
// either the task-mention class or a bare /u/<id> href, so both freshly
// rendered chips and server-sanitized ones round-trip.
function mentionUserId(node) {
    if (node.classList && node.classList.contains("task-mention")) {
        const m = (node.getAttribute("href") || "").match(/\/u\/(\d+)$/)
        if (m) return m[1]
    }
    const href = node.getAttribute("href") || ""
    const m = href.match(/^\/u\/(\d+)$/)
    return m ? m[1] : null
}

function serializeCode(node) {
    let content = node.textContent || ""
    if (content === "") return ""
    if (content.includes("``")) content = content.replace(/`/g, "")
    if (content === "") return ""
    return content.includes("`") ? `\`\`${content}\`\`` : `\`${content}\``
}
